import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { ViewUserDialogProps } from '../types';
import { UserActivityTab } from './UserActivityTab';
import { UserPaymentsTab } from './UserPaymentsTab';
import { UserDevicesTab } from './UserDevicesTab';
import { UserSecurityTab } from './UserSecurityTab';

type UserTab = 'overview' | 'activity' | 'payments' | 'devices' | 'security';

const TAB_LABELS: Record<UserTab, string> = {
  overview: 'Обзор',
  activity: 'Активность',
  payments: 'Оплаты',
  devices: 'Устройства',
  security: 'Журнал входов',
};

export function ViewUserDialog({
  isDark,
  isOpen,
  onOpenChange,
  user,
  isLoading,
  formatDate,
}: ViewUserDialogProps) {
  const [tab, setTab] = useState<UserTab>('overview');
  // Вкладка грузит данные при первом открытии и остаётся смонтированной, чтобы
  // переключение туда-обратно не дёргало сервер повторно
  const [visited, setVisited] = useState<Set<UserTab>>(() => new Set(['overview']));
  const [visitedFor, setVisitedFor] = useState<string | undefined>(user?.id);
  if (visitedFor !== user?.id) {
    setVisitedFor(user?.id);
    setVisited(new Set([tab]));
  }

  const selectTab = (next: UserTab) => {
    setTab(next);
    setVisited((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
  };

  const loginMethods = user
    ? [
        user.telegramId && 'Telegram',
        user.googleId && 'Google',
        user.appleId && 'Apple',
        user.email && 'Email',
      ].filter(Boolean) as string[]
    : [];
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Детали пользователя</DialogTitle>
          <DialogDescription className={cn(
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>
            Полная информация и лимиты
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
          </div>
        ) : user ? (
          <Tabs value={tab} onValueChange={(v) => selectTab(v as UserTab)}>
            <TabsList className={cn('rounded-xl p-1 mb-2 flex-wrap h-auto', isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]')}>
              {(Object.keys(TAB_LABELS) as UserTab[]).map((key) => (
                <TabsTrigger key={key} value={key} className="rounded-lg">{TAB_LABELS[key]}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="overview" className="space-y-6 py-4 mt-0">
            {/* Contacts & login */}
            <UserDetailsSection title="Контакты и вход" isDark={isDark}>
              <DetailItem label="Телефон" isDark={isDark}>
                {user.phone ? (
                  <a href={`tel:${user.phone}`} className="text-sm text-[#C9A227] hover:underline">{user.phone}</a>
                ) : '—'}
              </DetailItem>
              <DetailItem label="Telegram" isDark={isDark}>
                {user.telegramId ? (
                  <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                    {user.telegramUsername ? (
                      <a
                        href={`https://t.me/${user.telegramUsername}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#C9A227] hover:underline"
                      >
                        @{user.telegramUsername}
                      </a>
                    ) : 'без username'}
                    <span className={cn('ml-2 font-mono text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                      id {user.telegramId}
                    </span>
                  </p>
                ) : '—'}
              </DetailItem>
              <DetailItem label="Способы входа" isDark={isDark}>
                {loginMethods.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {loginMethods.map((m) => (
                      <Badge key={m} className="rounded-lg font-medium border-0 bg-blue-500/10 text-blue-500">{m}</Badge>
                    ))}
                  </div>
                ) : '—'}
              </DetailItem>
              <DetailItem label="Ник в играх" isDark={isDark}>
                {user.gamerTag || '—'}
              </DetailItem>
              {user.isBlocked && (
                <DetailItem label="Блокировка" isDark={isDark}>
                  <Badge className="rounded-lg font-medium border-0 bg-red-500/10 text-red-500">Заблокирован</Badge>
                </DetailItem>
              )}
              {user.mergedIntoUserId && (
                <DetailItem label="Склеен в аккаунт" isDark={isDark} mono breakAll>
                  {`${user.mergedIntoUserId} (${formatDate(user.mergedAt)})`}
                </DetailItem>
              )}
            </UserDetailsSection>

            {/* Basic Information */}
            <UserDetailsSection title="Основное" isDark={isDark}>
              <DetailItem label="User ID" isDark={isDark} mono breakAll>
                {user.id}
              </DetailItem>
              <DetailItem label="Username" isDark={isDark}>
                {user.username}
              </DetailItem>
              <DetailItem label="Email" isDark={isDark}>
                {user.email || '—'}
              </DetailItem>
              <DetailItem label="Role" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.role === 'admin'
                    ? 'bg-purple-500/10 text-purple-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.role}
                </Badge>
              </DetailItem>
              <DetailItem label="First Name" isDark={isDark}>
                {user.firstName || '—'}
              </DetailItem>
              <DetailItem label="Last Name" isDark={isDark}>
                {user.lastName || '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Status Information */}
            <UserDetailsSection title="Статус" isDark={isDark}>
              <DetailItem label="Subscription" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.isSubscribed
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-slate-500/10 text-slate-500'
                )}>
                  {user.isSubscribed ? 'С подпиской' : 'Бесплатный'}
                </Badge>
              </DetailItem>
              <DetailItem label="Account Type" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.is_anonymous
                    ? 'bg-amber-500/10 text-amber-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.is_anonymous ? 'Аноним' : 'Зарегистрирован'}
                </Badge>
              </DetailItem>
              <DetailItem label="Subscription End" isDark={isDark}>
                {formatDate(user.subscribedEnd)}
              </DetailItem>
              <DetailItem label="Last Login" isDark={isDark}>
                {formatDate(user.lastLogin)}
              </DetailItem>
              {user.isDeleted && (
                <DetailItem label="Удалён" isDark={isDark}>
                  <Badge className="rounded-lg font-medium border-0 bg-red-500/10 text-red-500">
                    {formatDate(user.deletedAt)}
                  </Badge>
                </DetailItem>
              )}
            </UserDetailsSection>

            {/* Goal & Deadline */}
            <UserDetailsSection title="Цель и срок" isDark={isDark}>
              <DetailItem label="Goal" isDark={isDark}>
                {user.goal ? (
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.goal === 'first_exam'
                      ? 'bg-blue-500/10 text-blue-500'
                      : user.goal === 'retake'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-purple-500/10 text-purple-500'
                  )}>
                    {user.goal === 'first_exam' ? 'Первый экзамен' : 
                     user.goal === 'retake' ? 'Пересдача' : 'Повтор'}
                  </Badge>
                ) : '—'}
              </DetailItem>
              <DetailItem label="Deadline" isDark={isDark}>
                {user.deadline ? (
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.deadline === '3_days'
                      ? 'bg-red-500/10 text-red-500'
                      : user.deadline === '1_week'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-emerald-500/10 text-emerald-500'
                  )}>
                    {user.deadline === '3_days' ? '3 Days' : 
                     user.deadline === '1_week' ? '1 Week' : '1 Month'}
                  </Badge>
                ) : '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Device & OAuth IDs */}
            <UserDetailsSection title="Устройство и OAuth" isDark={isDark} cols={3}>
              <DetailItem label="Device ID" isDark={isDark} mono breakAll>
                {user.deviceId || '—'}
              </DetailItem>
              <DetailItem label="Google ID" isDark={isDark} mono breakAll>
                {user.googleId || '—'}
              </DetailItem>
              <DetailItem label="Apple ID" isDark={isDark} mono breakAll>
                {user.appleId || '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Limits Information */}
            {user.limits && (
              <UserDetailsSection title="Лимиты" isDark={isDark}>
                <DetailItem label="Mock Exam Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.mock_exam_attempts_used}
                    remaining={user.limits.mock_exam_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Wrong Exam Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.wrong_exam_attempts_used}
                    remaining={user.limits.wrong_exam_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Marathon Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.marathon_attempts_used}
                    remaining={user.limits.marathon_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Premium Status" isDark={isDark}>
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.limits.isPremium
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : 'bg-slate-500/10 text-slate-500'
                  )}>
                    {user.limits.isPremium ? 'Премиум' : 'Бесплатный'}
                  </Badge>
                </DetailItem>
              </UserDetailsSection>
            )}

            {/* Dates */}
            <UserDetailsSection title="Даты" isDark={isDark}>
              <DetailItem label="Created At" isDark={isDark}>
                {formatDate(user.created_at)}
              </DetailItem>
              <DetailItem label="Updated At" isDark={isDark}>
                {formatDate(user.updated_at)}
              </DetailItem>
            </UserDetailsSection>
            </TabsContent>

            {/* Остальные вкладки — вручную вместо TabsContent: держим смонтированными после первого открытия */}
            {visited.has('activity') && (
              <div className={cn('py-4', tab !== 'activity' && 'hidden')}>
                <UserActivityTab userId={user.id} isDark={isDark} />
              </div>
            )}
            {visited.has('payments') && (
              <div className={cn('py-4', tab !== 'payments' && 'hidden')}>
                <UserPaymentsTab userId={user.id} isDark={isDark} />
              </div>
            )}
            {visited.has('devices') && (
              <div className={cn('py-4', tab !== 'devices' && 'hidden')}>
                <UserDevicesTab userId={user.id} isDark={isDark} />
              </div>
            )}
            {visited.has('security') && (
              <div className={cn('py-4', tab !== 'security' && 'hidden')}>
                <UserSecurityTab userId={user.id} isDark={isDark} />
              </div>
            )}
          </Tabs>
        ) : null}
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper Components
interface UserDetailsSectionProps {
  title: string;
  isDark: boolean;
  cols?: 2 | 3;
  children: React.ReactNode;
}

function UserDetailsSection({ title, isDark, cols = 2, children }: UserDetailsSectionProps) {
  return (
    <div>
      <h3 className={cn(
        'text-lg font-semibold mb-4',
        isDark ? 'text-white' : 'text-[#111111]'
      )}>{title}</h3>
      <div className={cn(
        'grid gap-4',
        cols === 3 ? 'grid-cols-3' : 'grid-cols-2'
      )}>
        {children}
      </div>
    </div>
  );
}

interface DetailItemProps {
  label: string;
  isDark: boolean;
  mono?: boolean;
  breakAll?: boolean;
  children: React.ReactNode;
}

function DetailItem({ label, isDark, mono, breakAll, children }: DetailItemProps) {
  const isTextContent = typeof children === 'string' || typeof children === 'number';
  
  return (
    <div className="space-y-1">
      <p className={cn(
        'text-xs font-medium',
        isDark ? 'text-slate-400' : 'text-slate-500'
      )}>{label}</p>
      {isTextContent ? (
        <p className={cn(
          'text-sm',
          isDark ? 'text-slate-300' : 'text-slate-700',
          mono && 'font-mono',
          breakAll && 'break-all'
        )}>{children}</p>
      ) : children}
    </div>
  );
}

interface LimitDisplayProps {
  used: number;
  remaining: number;
  isDark: boolean;
}

function LimitDisplay({ used, remaining, isDark }: LimitDisplayProps) {
  return (
    <p className={cn(
      'text-sm',
      isDark ? 'text-slate-300' : 'text-slate-700'
    )}>
      Used: {used}
      {remaining >= 0 && (
        <span className={cn(
          'ml-2',
          isDark ? 'text-slate-400' : 'text-slate-500'
        )}>
          (Remaining: {remaining})
        </span>
      )}
      {remaining === -1 && (
        <span className="ml-2 text-emerald-500">Без лимита</span>
      )}
    </p>
  );
}
